/**
 * adapters — adaptadores de datos para las primitivas del espacio de trabajo.
 *
 * Funciones puras (sin estado, sin efectos) que traducen el modelo de dominio
 * `ProcessedInfrastructure` al modelo presentacional de cada primitiva del
 * Bloque P. A3x-2: `ProcessedInfrastructure[] → FilaAtributo[]` para alimentar
 * la `AttributeTable` montada en la ranura `panelInf` del `WorkspaceShell`
 * (display, solo lectura). La selección/edición bidireccional mapa↔tabla es
 * posterior (A3x-3/4); aquí solo se proyecta el dato.
 *
 * Convención de «ubicada» coherente con el adaptador del mapa
 * (`MapViewer/adapters`): una infraestructura está ubicada cuando tiene ambas
 * coordenadas finales presentes y finitas (excluye 0/null/NaN/Infinity, que el
 * overview del mapa también descarta).
 *
 * VIS-3c · **catálogos que este módulo consulta** (`HX-SELLO-DECLARA-CONSUMIDOR`):
 * las funciones siguen siendo puras —sin estado ni efectos— pero ya no son
 * proyecciones ciegas: indexan dos catálogos estáticos para resolver aquí lo que
 * la primitiva no debe conocer (DOP selladas 01-ago).
 *
 * 1. `mapTipologia` (`services/export/toProcessedRecord`) → macro `TipologiaPTEL`.
 *    Es **el mismo resolutor que produce el entregable municipal**: medido en
 *    `VIS3C-S2` reproduce la columna TIPOLOGIA del export 23/23 sobre el corpus
 *    de Benizalón. Se elige por eso, y no `detectTipologia` del emparejador de
 *    símbolos, que discrepa del entregable en el 21,7 % de las filas
 *    (`OBS-CLASIF-DOS-CADENAS-TIPOLOGIA-DIVERGENTES`, sev B).
 * 2. `SIMBOLOGIA` (`design-system/v2`) → `colorIdentidad` de la macro (19/19).
 * 3. `colors.scores` (DS v2, umbrales 85/70/50) → nivel de la píldora de *score*.
 *    Umbrales **del catálogo del DS**, no acuñados aquí (`VIS-3c` no los re-litiga).
 *
 * @version 1.1.0
 */

import type { ProcessedInfrastructure } from '@/types/processing';
import { SIMBOLOGIA } from '@/design-system/v2/simbologia';
import { mapTipologia } from '@/services/export/toProcessedRecord';
import type { EstadoFila, FilaAtributo, NivelScore } from './AttributeTable';
import { clasificarEstadoFila } from '../shared/estadoFila';

/**
 * ¿La infraestructura tiene una coordenada final utilizable (X e Y)?
 * Truthy + finita: excluye 0, null, undefined, NaN e Infinity. Coherente con
 * el predicado del overview del mapa (`!infra.xFinal || !infra.yFinal`).
 */
function tieneCoordFinal(infra: ProcessedInfrastructure): boolean {
  const { xFinal, yFinal } = infra;
  return (
    !!xFinal && !!yFinal &&
    Number.isFinite(xFinal) && Number.isFinite(yFinal)
  );
}

/**
 * Estado presentacional de la fila. P2VOC-b: sale de la clasificación única
 * (`shared/estadoFila`): `ubicada` | `por_revisar` | `sin_coordenada`. La fila
 * cuya geocodificación falló y no tiene coordenada es `sin_coordenada` (el
 * antiguo estado `error` desaparece).
 */
function derivarEstado(infra: ProcessedInfrastructure): EstadoFila {
  return clasificarEstadoFila(infra);
}

/**
 * Resuelve el nivel de la píldora de *score* con los umbrales del DS v2
 * (85/70/50, los mismos que `colors.scores`). Sin *score* no hay nivel: la
 * primitiva pinta el guion, no una píldora de un dato ausente.
 */
export function derivarNivelScore(score?: number): NivelScore | undefined {
  if (score == null || !Number.isFinite(score)) return undefined;
  if (score >= 85) return 'alto';
  if (score >= 70) return 'medio';
  if (score >= 50) return 'bajo';
  return 'critico';
}

/**
 * Resuelve el color de identidad de la tipología: `tipo` libre del documento →
 * macro `TipologiaPTEL` (vía `mapTipologia`, total: cae a `GENERICO`) →
 * `SIMBOLOGIA[macro].colorIdentidad`. Sin `tipo` no hay color, porque no hay
 * dato — el hueco es ausencia, no clave irresoluble (`DOP-VIS3C-S2-HUECO`=a).
 */
export function derivarColorTipologia(tipo?: string, nombre?: string): string | undefined {
  if (tipo == null || tipo.trim() === '') return undefined;
  // TIPUNI (12-sep-2026): `mapTipologia` lee también el nombre, la misma cascada
  // que la tabla de P1 y la exportación de P4 — un elemento, una tipología.
  return SIMBOLOGIA[mapTipologia(tipo, nombre)].colorIdentidad;
}

/**
 * Proyecta una `ProcessedInfrastructure` a una `FilaAtributo` (display).
 * No filtra: conserva también las pendientes/erróneas para que la tabla muestre
 * el inventario completo (el mapa solo pinta las ubicadas).
 */
export function toFilaAtributo(infra: ProcessedInfrastructure): FilaAtributo {
  const ubicada = tieneCoordFinal(infra);
  return {
    id: infra.id,
    nombre: infra.nombre,
    estado: derivarEstado(infra),
    coord: ubicada ? { x: infra.xFinal as number, y: infra.yFinal as number } : undefined,
    tipologia: infra.tipo || undefined,
    tipologiaColor: derivarColorTipologia(infra.tipo, infra.nombre),
    score: infra.score,
    scoreNivel: derivarNivelScore(infra.score),
  };
}

/**
 * Proyecta un conjunto completo de `ProcessedInfrastructure` a `FilaAtributo[]`,
 * preservando el orden de entrada (sin filtrar).
 */
export function toFilasAtributo(
  infrastructures: ProcessedInfrastructure[],
): FilaAtributo[] {
  return infrastructures.map(toFilaAtributo);
}
